import { Box, Typography, CardContent } from "@mui/material";
import { Schedule, TrendingFlat } from "@mui/icons-material";
import styled, { css } from "styled-components";
import { SPACING, QUERIES } from "../../../utils/constants";
import { SignedProps } from "./types";
import { StyledCard, cardFocusRing } from "../styles";
import { BaseButton } from "../../Button";

export const AccountCardSurface = styled(StyledCard)`
  && {
    position: relative;

    &:has(:focus-visible) {
      ${cardFocusRing}
    }
  }
`;

export const AccountOpenButton = styled(BaseButton)`
  display: block;
  width: 100%;
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  text-align: inherit;
  cursor: pointer;

  /* Override the global dark-theme tracking applied to button elements. */
  && {
    letter-spacing: inherit;
  }

  /* Keep truncation off the control so its overlay can cover the whole card. */
  && > span {
    display: block;
    line-height: inherit;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 1;
  }

  /* The card surface supplies the focus ring around the entire hit area. */
  &:focus-visible {
    outline: none;
    box-shadow: none;
  }
`;

export const CardContentWrapper = styled(CardContent)`
  && {
    padding: ${SPACING.lg}px;

    &:last-child {
      padding-bottom: ${SPACING.lg}px;
    }

    @media ${QUERIES.tabletLargeAndDown} {
      &:last-child {
        padding-bottom: ${SPACING.md}px;
      }
    }
  }
`;

export const AccountHeaderRow = styled(Box)`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${SPACING.sm + SPACING.xs}px;
  margin-bottom: ${SPACING.md + SPACING.xs}px;

  @media ${QUERIES.tabletLargeAndDown} {
    margin-bottom: ${SPACING.xs + SPACING.xxs}px;
    align-items: center;
  }
`;

export const AccountTitle = styled.h2`
  margin: 0;
  font-size: var(--text-xxxl);
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.015em;
  color: var(--color-text-default);
  flex: 1;
  min-width: 0;

  @media ${QUERIES.tabletLargeAndDown} {
    font-size: var(--text-xl);
  }
`;

export const InactiveBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${SPACING.xs + SPACING.xxs}px;
  padding: ${SPACING.xs}px ${SPACING.sm + SPACING.xxs}px;
  border-radius: 20px;
  border: 1px solid rgba(148, 163, 184, 0.28);
  background: rgba(148, 163, 184, 0.09);
  color: var(--color-text-secondary);
  font-size: var(--text-xs);
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  white-space: nowrap;
  flex-shrink: 0;
  margin-top: ${SPACING.xs + SPACING.xxs}px;
`;

export const InactiveDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--color-text-secondary);
  flex-shrink: 0;
  display: inline-block;
`;

export const InactivityBanner = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm + SPACING.xxs}px;
  padding: ${SPACING.lg + SPACING.xs}px
    ${SPACING.sm + SPACING.xs + SPACING.xxs}px;
  border-radius: 8px;
  background: rgba(148, 163, 184, 0.07);
  border: 1px solid var(--color-card-metric-border);
  margin-bottom: ${SPACING.lg}px;
  margin-top: ${SPACING.lg}px;
  color: var(--color-text-secondary);
  font-size: var(--text-sm);
  line-height: 1.4;

  @media ${QUERIES.tabletLargeAndDown} {
    display: none;
  }
`;

export const BalanceSection = styled(Box)`
  margin-bottom: ${SPACING.md}px;

  @media ${QUERIES.tabletLargeAndDown} {
    margin-bottom: ${SPACING.sm}px;
  }
`;

export const BalanceLabel = styled.span`
  display: block;
  font-size: var(--text-xs);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  color: var(--color-text-secondary);
  margin-bottom: ${SPACING.xs}px;
`;

export const BalanceAmount = styled(Typography)<SignedProps>`
  && {
    font-size: var(--text-display);
    font-weight: 800;
    line-height: 1.1;
    letter-spacing: -0.02em;
    color: ${({ $positive, $inactive }) =>
      $inactive || $positive
        ? "var(--color-text-default)"
        : "var(--color-error)"};

    @media ${QUERIES.tabletLargeAndDown} {
      font-size: var(--text-xxxl);
    }
  }
`;

export const SparklineWrapper = styled(Box)`
  height: 64px;
  margin: 0 -${SPACING.xxs}px;

  @media ${QUERIES.tabletLargeAndDown} {
    height: 48px;
  }
`;

export const SparklineMeta = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: ${SPACING.sm}px;
  margin-bottom: ${SPACING.md}px;

  @media ${QUERIES.tabletLargeAndDown} {
    display: none;
  }
`;

export const SparklineMetaLabel = styled.span`
  font-size: var(--text-xs);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  color: var(--color-text-secondary);
`;

export const SparklineDelta = styled.span<SignedProps>`
  font-size: var(--text-base);
  font-weight: 700;
  color: ${({ $positive }) =>
    $positive ? "var(--color-success)" : "var(--color-error)"};
`;

export const NoHistoryNote = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm}px;
  padding: ${SPACING.md + SPACING.xs}px
    ${SPACING.sm + SPACING.xs + SPACING.xxs}px;
  border-radius: 8px;
  background: rgba(148, 163, 184, 0.07);
  border: 1px solid var(--color-card-metric-border);
  margin-top: ${SPACING.sm}px;
  margin-bottom: ${SPACING.md}px;
  color: var(--color-text-secondary);
  font-size: var(--text-sm);
  line-height: 1.4;

  @media ${QUERIES.tabletLargeAndDown} {
    padding: ${SPACING.sm + SPACING.xs}px;
    margin-top: ${SPACING.xs + SPACING.xxs}px;
    margin-bottom: ${SPACING.sm}px;
  }
`;

// The leading icon of a note row. Both notes drew the identical `sx` object inline, which the
// styling rules forbid and which drifted the moment one of them was edited.
const noteIcon = css`
  && {
    font-size: var(--text-md);
    color: var(--color-text-secondary);
    flex-shrink: 0;
  }
`;

export const NoHistoryIcon = styled(TrendingFlat)`
  ${noteIcon}
`;

export const InactivityIcon = styled(Schedule)`
  ${noteIcon}
`;

export const MetricsGrid = styled(Box)`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding-top: ${SPACING.sm}px;
  border-top: 1px solid var(--color-card-metric-border);
  gap: ${SPACING.xl}px;

  @media ${QUERIES.tabletLargeAndDown} {
    display: none;
  }
`;

export const MetricBox = styled(Box)`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

export const MetricLabel = styled.span`
  display: flex;
  align-items: center;
  gap: ${SPACING.xs}px;
  font-size: var(--text-xs);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  color: var(--color-text-secondary);
`;

export const MetricValue = styled.span`
  font-size: var(--text-base);
  font-weight: 700;
  color: var(--color-text-default);
`;

export const MobileDelta = styled.span<SignedProps>`
  display: none;

  @media ${QUERIES.tabletLargeAndDown} {
    display: inline-block;
    font-size: var(--text-sm);
    font-weight: 700;
    white-space: nowrap;
    flex-shrink: 0;
    color: ${({ $positive }) =>
      $positive ? "var(--color-success)" : "var(--color-error)"};
  }
`;

export const CompactMeta = styled.span`
  display: none;

  @media ${QUERIES.tabletLargeAndDown} {
    display: block;
    font-size: var(--text-xs);
    color: var(--color-text-secondary);
    padding-top: ${SPACING.sm}px;
    border-top: 1px solid var(--color-card-metric-border);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;
